import React from "react";
import { Link } from "react-router-dom";
import { MapPin } from "lucide-react";
import { SEO } from "../components/SEO";

const districts = [
  { state: "Bihar", district: "Patna", titles: 3, focus: "Political Biography", books: ["The Half of the Nation Speaks"] },
  { state: "Gujarat", district: "Vadnagar", titles: 2, focus: "Statesmen", books: ["I Am India"] },
  { state: "Uttar Pradesh", district: "Lucknow", titles: 4, focus: "Contemporary Democracy", books: ["The Half of the Nation Speaks"] },
  { state: "Tamil Nadu", district: "Rameswaram", titles: 2, focus: "Science & Leadership", books: ["The Man Who Gave Missiles to India"] },
  { state: "Punjab", district: "Amritsar", titles: 1, focus: "Election Analysis", books: ["The BJP Window in Punjab"] },
  { state: "Delhi", district: "New Delhi", titles: 5, focus: "Central Government", books: ["I Am India"] },
  { state: "Kerala", district: "Kochi", titles: 2, focus: "Gulf Diaspora", books: ["The Indian Pillars of Dubai"] },
  { state: "Maharashtra", district: "Mumbai", titles: 4, focus: "Enterprise & Culture", books: ["The Republic of Children"] },
  { state: "West Bengal", district: "Kolkata", titles: 2, focus: "Culture & Cinema", books: [] },
];

const diaspora = [
  { region: "UAE — Dubai", indians: "3.5 million", featured: "The Indian Pillars of Dubai" },
  { region: "UK — London", indians: "1.8 million", featured: "Global co-editions coming 2026" },
  { region: "USA — Northeast", indians: "4.4 million", featured: "Bharat Icon Series (deluxe editions)" },
  { region: "Singapore", indians: "650,000", featured: "Diaspora & Enterprise imprint" },
];

export default function Districts() {
  return (
    <div data-testid="districts-page">
      <SEO
        title="Districts & Diaspora"
        path="/districts"
        description="Sponsor a district. Preserve a story. Underwrite a volume. CBH partners with Indian districts and diaspora chapters to commission place-based books."
        keywords="district sponsorship, Indian diaspora, CBH Districts, book underwriting India"
      />
      {/* Hero */}
      <section className="pt-16 lg:pt-24 pb-20 grain-overlay">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-6">§ Districts & Diaspora</p>
          <h1 className="font-editorial text-6xl md:text-8xl lg:text-[8rem] leading-[0.88] tracking-tighter text-ink text-balance">
            One country, <em className="text-crimson not-italic">seven hundred stories.</em>
          </h1>
          <p className="mt-10 max-w-3xl text-lg leading-relaxed text-ink-muted">
            CBH&rsquo;s Districts programme partners with Indian districts and diaspora chapters to
            commission books grounded in place — biographies, economic histories, cultural
            portraits. Sponsor a district. Preserve a story. Underwrite a volume.
          </p>
        </div>
      </section>

      {/* Districts grid */}
      <section className="py-16" data-testid="districts-grid-section">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-4">§ Featured Districts</p>
              <h2 className="font-editorial text-4xl md:text-5xl text-ink leading-tight">Places CBH publishes from.</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-crimson/20">
            {districts.map((d, i) => (
              <div key={i} className="bg-ivory p-8 group cursor-default" data-testid={`district-card-${d.state.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="flex items-start justify-between mb-6">
                  <MapPin strokeWidth={1} className="w-6 h-6 text-crimson" />
                  <span className="text-[10px] uppercase tracking-[0.28em] text-gold-dark">{d.titles} titles</span>
                </div>
                <h3 className="font-editorial text-2xl text-ink mb-1">{d.district}</h3>
                <p className="text-[11px] uppercase tracking-[0.24em] text-ink-muted mb-4">{d.state}</p>
                <p className="text-sm text-ink-muted mb-6">{d.focus}</p>
                {d.books.length > 0 && (
                  <p className="editorial-italic text-sm text-crimson">
                    {d.books.slice(0, 1).map((b) => `“${b}”`)}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Diaspora */}
      <section className="py-24 bg-ivory-soft grain-overlay" data-testid="districts-diaspora-section">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-4">§ The Diaspora Portal</p>
          <h2 className="font-editorial text-5xl md:text-6xl text-ink leading-tight mb-16 text-balance">
            Wherever <em className="text-crimson not-italic">Bharat</em> travels.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {diaspora.map((d, i) => (
              <div key={i} className="border border-crimson/20 bg-ivory p-8">
                <h3 className="font-editorial text-2xl text-ink mb-2">{d.region}</h3>
                <p className="text-[11px] uppercase tracking-[0.24em] text-gold-dark mb-6">
                  {d.indians} Indians · Chapter Active
                </p>
                <p className="editorial-italic text-lg text-ink-soft">{d.featured}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-ink text-ivory grain-overlay">
        <div className="mx-auto max-w-[1200px] px-6 lg:px-10 text-center">
          <p className="text-[11px] uppercase tracking-[0.32em] text-gold-light mb-6">§ Partner With Us</p>
          <h2 className="font-editorial text-5xl md:text-7xl leading-[0.95] tracking-tight text-balance">
            Sponsor a district. <em className="text-gold-light not-italic">Preserve a story.</em>
          </h2>
          <p className="mt-8 max-w-2xl mx-auto text-ivory/70 text-lg leading-relaxed">
            Every district-sponsored volume is co-branded, deposit-copied to state libraries,
            and preserved in the CBH Bharat Archive.
          </p>
          <Link
            to="/contact?inquiry_type=schools"
            data-testid="districts-cta"
            className="mt-10 inline-flex items-center gap-3 bg-ivory text-crimson px-8 py-4 text-sm uppercase tracking-[0.24em] hover:bg-gold-light hover:text-ink transition-colors duration-500"
          >
            Enquire About Sponsorship →
          </Link>
        </div>
      </section>
    </div>
  );
}
